import { ComponentType, render, h } from "preact"
import { ConfigContext, FullConfig } from "../config"
import { Store } from "../state"
import { logger, measure } from "@nosto/search-js/utils"
import ErrorBoundary from "../components/ErrorBoundary"
import { waitForElements } from "../utils/wait"
import { extractNativeContent } from "../helpers/searchFallback"
import { CategoryPageProvider } from "@nosto/search-js/preact/category"
import { SearchPageProvider } from "@nosto/search-js/preact/serp"

export async function renderComponent(
    config: FullConfig,
    store: Store,
    {
        cssSelector,
        docComponent
    }: {
        cssSelector?: string
        docComponent?: ComponentType
    }
): Promise<void> {
    if (!cssSelector || !docComponent) {
        return
    }

    const targets = await waitForElements({
        selector: cssSelector,
        timeout: 100
    })

    const target = targets[0]

    if (!target) {
        logger.warn(`No content element with ${cssSelector} selector found, skipping rendering`)
        return
    }

    if (targets.length > 1) {
        logger.warn(
            `Multiple content elements found with ${cssSelector} selector, using the first element for rendering`
        )
    }

    if (config.serpRenderDelay) {
        await config.serpRenderDelay()
    }

    extractNativeContent(target)
    const end = measure("renderSearch")

    const pageType = store.getState().pageType
    const renderProps = {
        store,
        config,
        target,
        ComponentToRender: docComponent
    } satisfies RenderProps

    if (pageType === "search") {
        renderSerpComponent(renderProps)
    } else if (pageType === "category") {
        renderCategoryComponent(renderProps)
    } else {
        throw new Error(`Unsupported page type: ${pageType}`)
    }

    end()
}

interface RenderProps {
    store: Store
    config: FullConfig
    target: Element
    ComponentToRender: ComponentType
}

function renderSerpComponent({ store, config, target, ComponentToRender }: RenderProps) {
    render(
        <ErrorBoundary>
            <SearchPageProvider store={store} config={config}>
                <ConfigContext.Provider value={config}>
                    <ComponentToRender />
                </ConfigContext.Provider>
            </SearchPageProvider>
        </ErrorBoundary>,
        target
    )
}

function renderCategoryComponent({ store, config, target, ComponentToRender }: RenderProps) {
    render(
        <ErrorBoundary>
            <CategoryPageProvider store={store} config={config}>
                <ConfigContext.Provider value={config}>
                    <ComponentToRender />
                </ConfigContext.Provider>
            </CategoryPageProvider>
        </ErrorBoundary>,
        target
    )
}
